import { formatDate } from '../../../../../utils/formatUtils';
import { Claim, ClaimStatus } from '../../../../Interfaces/Insurance/Claim';
import { Panel } from 'primereact/panel';
import { Timeline } from 'primereact/timeline';
import { Card } from 'primereact/card';
import ExpandableText from '../../../../components/messages/ExpandableText';
import React, { useRef, useState } from 'react';
import ConfirmationDialog from '../../../../components/messages/ConfirmationDialog';
import LoadingButton from '../../../../components/inputs/LoadingButton';
import TypeInConfirmationFields from '../../../../components/dialog/TypeInConfirmationFields';
import { Toast } from 'primereact/toast';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import ErrorToast, {
  showToast,
} from '../../../../components/messages/ErrorAlert';
import { removeClaim } from '../../../../services/Insurance/Claim';
import { useClaimSummaryContext } from '../ClaimContext';
import { useRolesAccessContext } from '@/src/app/context/RolesAccessContext';
import ClaimAttachment from './ClaimAttachment';
import ClaimLogAttachment from './ClaimLogAttachment';
import { useCompanyContext } from '@/src/app/context/CompanyContext';

type ClaimDataProps = {
  claim: Claim;
  afterRemoval: () => void;
  disableEdit?: boolean;
};

const ClaimData = ({ claim, afterRemoval, disableEdit }: ClaimDataProps) => {
  const { selectedCompany } = useCompanyContext();
  const { rolesAcess } = useRolesAccessContext();
  const toast = useRef<Toast>(null);
  const { removeOne } = useClaimSummaryContext();
  const [visibility, setVisibility] = useState(false);
  const access = rolesAcess?.find((access) => access.report === 'claim');
  const timelineValues = [
    { date: claim.createdAt, description: 'Claim Created' },
    ...(claim.logs ?? []),
    ...(claim.status === ClaimStatus.Closed
      ? [{ date: claim.updatedAt, description: 'Claim Closed' }]
      : []),
  ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const removeRequest = useMutation({
    mutationFn: () => {
      return removeClaim(claim.id);
    },
    onSuccess: () => {
      removeOne(claim.id);
      afterRemoval();
    },
    onError: (error: AxiosError) => {
      const response = error.response;
      const data = response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Claim Removal Error',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  if (removeRequest.isSuccess) {
    return (
      <div className="w-full mx-auto flex flex-column gap-5">
        <div className="w-fit mx-auto flex flex-column">
          <div className="text-center text-40px font-bold text-green-500 mb-3">
            Success!
          </div>
          <i className="pi pi-check-circle mx-auto text-40px text-green-500 mb-3" />
          <div className="text-standard mt-3 mx-auto">
            The Claim has been removed successfully!
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="grid mx-3">
        <div className="col-12 sm:col-6 print-col-6">
          <label className="text-18px block mb-2 font-bold">Policy:</label>
          {claim.policy.policy}
        </div>
        <div className="col-12 sm:col-6 print-col-6">
          <label className="text-18px block mb-2 font-bold">Type:</label>
          {claim.type ?? '-'}
        </div>
        {claim.employee && (
          <div className="col-12 sm:col-6 print-col-6">
            <label className="text-18px block mb-2 font-bold">Employee:</label>
            {claim.employee.name ?? '-'}
          </div>
        )}
        {claim.subcontractor && (
          <div className="col-12 sm:col-6 print-col-6">
            <label className="text-18px block mb-2 font-bold">
              Subcontractor:
            </label>
            {claim.subcontractor.name ?? '-'}
          </div>
        )}
        {claim.other && (
          <div className="col-12 sm:col-6 print-col-6">
            <label className="text-18px block mb-2 font-bold">Other:</label>
            {claim.other ?? '-'}
          </div>
        )}
        <div className="col-12 sm:col-6 print-col-6">
          <label className="text-18px block mb-2 font-bold">Claim #:</label>
          {claim.number ?? '-'}
        </div>
        {(claim.contacts?.length ?? 0) > 0 && (
          <div className="col-12 pb-0">
            <label className="block font-bold text-20px">
              Insurance Contact Info
            </label>
          </div>
        )}
        <div className="col-12 flex flex-column gap-2">
          {claim.contacts?.map((contact, index) => (
            <Panel
              header={
                <label className="blackText text-18px">
                  Contact {index + 1}
                </label>
              }
              toggleable
              key={contact.id}
            >
              <div className="grid mx-3 blackText text-standard">
                <div className="col-12 sm:col-6 print-col-6">
                  <label className="text-18px block mb-2 font-bold">
                    Name:
                  </label>
                  {contact.name}
                </div>
                <div className="col-12 sm:col-6 print-col-6">
                  <label className="text-18px block mb-2 font-bold">
                    Phone:
                  </label>
                  {contact.phone ?? '-'}
                </div>
                <div className="col-12 sm:col-6 print-col-6">
                  <label className="text-18px block mb-2 font-bold">
                    Email:
                  </label>
                  {contact.email ?? '-'}
                </div>
                <div className="col-12">
                  <label className="text-18px block mb-2 font-bold">
                    Details:
                  </label>
                  {contact.details ?? '-'}
                </div>
              </div>
            </Panel>
          ))}
        </div>
        <div className="col-12 pb-0">
          <label className="text-standard block font-bold text-20px">
            Accident Info
          </label>
        </div>
        <div className="col-12 sm:col-6 print-col-6">
          <label className="text-18px block mb-2 font-bold">Date:</label>
          {formatDate(claim.accidentDate)}
        </div>
        <div className="col-12 sm:col-6 print-col-6 capitalize">
          <label className="text-18px block mb-2 font-bold">Status:</label>
          {claim.status}
        </div>
        <div className="col-12">
          <label className="text-18px block mb-2 font-bold">Description:</label>
          {claim.accidentDescription}
        </div>
        <div className="col-12">
          <ClaimAttachment
            claimID={claim.id}
            attachmentName={claim.attachmentName ?? undefined}
          />
        </div>
        <div className="col-12 pb-0">
          <label className="text-standard block font-bold text-20px">
            Logs
          </label>
        </div>
        <div className="col-12 pb-0">
          <Timeline
            className="custom-right-timeline"
            value={timelineValues}
            content={(log) => (
              <div className="border-round-md border-300 border-1 p-3 mb-3">
                <div className="flex justify-content-between align-items-center">
                  <label className="text-18px block mb-2 font-bold">
                    {formatDate(log.date)}
                  </label>
                  {log.attachmentName && (
                    <ClaimLogAttachment
                      claimID={claim.id}
                      logID={log.id}
                      attachmentName={log.attachmentName}
                    />
                  )}
                </div>
                <ExpandableText text={log.description} />
              </div>
            )}
          />
        </div>
      </div>
      <ErrorToast toastRef={toast} />
      {access?.editable && selectedCompany?.isActive && !disableEdit && (
        <div className="flex flex-wrap flex-row-reverse gap-2 mb-3">
          <ConfirmationDialog
            tagKey="claim-removal"
            visibility={visibility}
            Button={
              <LoadingButton
                isLoading={removeRequest.isPending}
                label="Remove"
                className="w-8rem bluwaiRed border-0 flex m-auto py-3 printHide"
                fontSize="text-20px"
                type="button"
                onClick={() => setVisibility(true)}
              />
            }
            contentClassName="pb-0"
            acceptClassName="hidden"
            rejectClassName="hidden"
            onHide={() => setVisibility(false)}
            message={
              <TypeInConfirmationFields
                onReject={() => {
                  setVisibility(false);
                }}
                onConfirm={removeRequest.mutate}
                word="yes"
                label="Are you sure you want to remove the claim?"
              />
            }
          />
        </div>
      )}
    </React.Fragment>
  );
};

export default ClaimData;
